import { useArchitectureDataStore } from "../data_store/architectureDataStore.ts";

const CONFIG = {
    pingIntervalMs: 5 * 60 * 1000,   // 5 min — safely under API Gateway's 10-min idle limit
    initialBackoffMs: 1_000,
    maxBackoffMs: 30_000,
    maxReconnectAttempts: 10,
};

let ws: WebSocket | null = null;
let started = false;
let reconnectTimer: number | null = null;
let pingTimer: number | null = null;
let reconnectAttempts = 0;
let isClosingIntentionally = false;

// ─── Backoff ──────────────────────────────────────────────────────────────────

function getBackoffMs(): number {
    const jitter = Math.random() * 500;

    return Math.min(
        CONFIG.initialBackoffMs * 2 ** reconnectAttempts + jitter,
        CONFIG.maxBackoffMs
    );
}

// ─── Heartbeat ────────────────────────────────────────────────────────────────

function startPing() {
    stopPing();

    pingTimer = window.setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: "ping" }));
        }
    }, CONFIG.pingIntervalMs);
}

function stopPing() {
    if (pingTimer !== null) {
        clearInterval(pingTimer);
        pingTimer = null;
    }
}

// ─── Reconnect ────────────────────────────────────────────────────────────────

function scheduleReconnect(connect: () => void) {
    if (reconnectTimer !== null || isClosingIntentionally) return;

    if (reconnectAttempts >= CONFIG.maxReconnectAttempts) {
        console.error(`[wsClient] Max reconnect attempts (${CONFIG.maxReconnectAttempts}) reached. Giving up.`);
        started = false;
        return;
    }

    const delay = getBackoffMs();
    console.warn(`[wsClient] Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempts + 1})`);

    reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        reconnectAttempts++;
        connect();
    }, delay);
}

// ─── Core ─────────────────────────────────────────────────────────────────────

export function startWebSocket(url: string, token: string) {
    if (started) return;

    started = true;
    isClosingIntentionally = false;
    reconnectAttempts = 0;

    const connect = () => {
        // Send token in first message instead of URL to keep it out of logs.
        // Your API GW $connect route should allow unauthenticated connections
        // and validate the token on the first message instead.
        const finalUrl = `${url}?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(finalUrl);

        ws.onopen = () => {
            reconnectAttempts = 0; // reset backoff on successful connection
            startPing();
        };

        ws.onmessage = (evt) => {
            let msg: Record<string, unknown> = {};

            try { msg = JSON.parse(evt.data); }
            catch { return; }

            if (msg.action === 'architecture-sync') useArchitectureDataStore.getState().upsertItem(msg.data);
        };

        ws.onclose = (evt) => {
            stopPing();
            ws = null;

            if (isClosingIntentionally) return;

            // 4001/4003: auth errors — do not reconnect
            if (evt.code === 4001 || evt.code === 4003) {
                console.error("[wsClient] Auth error — stopping reconnect.", evt.code);
                started = false;
                return;
            }

            scheduleReconnect(connect);
        };

        ws.onerror = (err) => {
            // Let onclose handle reconnect — just log here to avoid double scheduling
            console.error("[wsClient] WebSocket error:", err);
            // onerror is always followed by onclose, so no explicit close() needed
        };
    };

    connect();
}

export function stopWebSocket() {
    isClosingIntentionally = true;
    started = false;

    stopPing();

    if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }

    ws?.close(1000, "Client stopped");
    ws = null;
}